import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, CheckCircle, Clock, XCircle, Lock } from 'lucide-react';
import api from '../lib/api';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_tenants: 0, active_tenants: 0, pending_tenants: 0, suspended_tenants: 0 });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, tenantsRes] = await Promise.all([
        api.get('/super-admin/dashboard', config),
        api.get('/super-admin/tenants', config),
      ]);

      setStats(statsRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (tenantId, action) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('superAdminToken');
      await api.post(`/super-admin/tenants/${tenantId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
      setSelectedTenant(null);
    } catch (error) {
      console.error(`Error ${action} tenant:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = async (tenantId) => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await api.get(`/super-admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedTenant(response.data);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    }
  };

  const handleDelete = async (tenantId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete "${companyName}"? This will permanently delete the tenant and all its users, freeing up their email addresses.`)) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('superAdminToken');
      await api.delete(`/super-admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
      setSelectedTenant(null);
    } catch (error) {
      console.error('Error deleting tenant:', error);
      alert('Failed to delete tenant.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    navigate('/super-admin');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('superAdminToken');
      await api.post('/super-admin/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setPasswordSuccess('Password changed successfully. Logging out...');
      setTimeout(() => {
        handleLogout();
      }, 1500);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="super-admin-dashboard">
      <header className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="text-[#4A7AE0] font-bold text-2xl tracking-tighter">SAMS - Super Admin</div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="bg-white text-[#1a1a2e] hover:bg-[#f9fafb] border border-[#e5e7eb] rounded-xl px-4 py-2 font-medium transition-colors flex items-center gap-2"
              data-testid="change-password-button"
            >
              <Lock className="w-4 h-4" />
              Change Password
            </button>
            <button
              onClick={handleLogout}
              className="bg-white text-[#1a1a2e] hover:bg-[#f9fafb] border border-[#e5e7eb] rounded-xl px-4 py-2 font-medium transition-colors"
              data-testid="logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {showChangePassword && (
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-6 mb-8 max-w-md" data-testid="change-password-form">
            <h3 className="text-lg font-bold text-[#1a1a2e] mb-4">Change Password</h3>
            <form onSubmit={handleChangePassword}>
              {passwordError && (
                <div className="mb-4 p-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-sm text-[#EF4444] text-sm" data-testid="password-error">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="mb-4 p-3 bg-[#DBEAFE] border border-[#93C5FD] rounded-xl text-[#1E40AF] text-sm" data-testid="password-success">
                  {passwordSuccess}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1a1a2e] mb-1">Current Password</label>
                <input
                  type="password"
                  data-testid="current-password-input"
                  className="w-full border border-[#e5e7eb] rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] bg-white text-[#1a1a2e]"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1a1a2e] mb-1">New Password</label>
                <input
                  type="password"
                  data-testid="new-password-input"
                  className="w-full border border-[#e5e7eb] rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] bg-white text-[#1a1a2e]"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1a1a2e] mb-1">Confirm New Password</label>
                <input
                  type="password"
                  data-testid="confirm-password-input"
                  className="w-full border border-[#e5e7eb] rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] bg-white text-[#1a1a2e]"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="submit-password-button"
                className="bg-[#1E40AF] text-white hover:bg-[#1e3a8a] rounded-xl px-4 py-2 font-semibold transition-colors"
              >
                Update Password
              </button>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-6" data-testid="total-tenants-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6b7280] mb-1">Total Tenants</p>
                <p className="text-3xl font-bold text-[#1a1a2e]">{stats.total_tenants}</p>
              </div>
              <Users className="w-8 h-8 text-[#4A7AE0]" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-6" data-testid="active-tenants-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6b7280] mb-1">Active</p>
                <p className="text-3xl font-bold text-[#4A7AE0]">{stats.active_tenants}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-[#4A7AE0]" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-6" data-testid="pending-tenants-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6b7280] mb-1">Pending</p>
                <p className="text-3xl font-bold text-[#F59E0B]">{stats.pending_tenants}</p>
              </div>
              <Clock className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.5} />
            </div>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-6" data-testid="suspended-tenants-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6b7280] mb-1">Suspended</p>
                <p className="text-3xl font-bold text-[#EF4444]">{stats.suspended_tenants}</p>
              </div>
              <XCircle className="w-8 h-8 text-[#EF4444]" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm" data-testid="tenants-table">
          <div className="px-6 py-4 border-b border-[#e5e7eb]">
            <h2 className="text-xl font-bold text-[#1a1a2e]">All Tenants</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                <tr>
                  <th className="py-3 px-4">Company Name</th>
                  <th className="py-3 px-4">City</th>
                  <th className="py-3 px-4">Plan</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Registered Date</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                    <td className="py-3 px-4 text-[#1a1a2e] font-medium">{tenant.company_name}</td>
                    <td className="py-3 px-4 text-[#1a1a2e]">{tenant.company_city || 'N/A'}</td>
                    <td className="py-3 px-4 text-[#1a1a2e] capitalize">{tenant.subscription_plan}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          tenant.subscription_status === 'active'
                            ? 'bg-[#DBEAFE] text-[#1E40AF]'
                            : tenant.subscription_status === 'pending'
                            ? 'bg-[#FEF3C7] text-[#92400E]'
                            : 'bg-[#FEE2E2] text-[#991B1B]'
                        }`}
                      >
                        {tenant.subscription_status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#1a1a2e]">{new Date(tenant.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDetails(tenant.id)}
                          className="text-[#1E40AF] hover:underline text-xs font-medium"
                          data-testid={`view-details-${tenant.id}`}
                        >
                          View
                        </button>
                        {tenant.subscription_status !== 'active' && (
                          <button
                            onClick={() => handleAction(tenant.id, 'activate')}
                            disabled={actionLoading}
                            className="text-[#4A7AE0] hover:underline text-xs font-medium disabled:opacity-50"
                            data-testid={`activate-${tenant.id}`}
                          >
                            Activate
                          </button>
                        )}
                        {tenant.subscription_status === 'active' && (
                          <button
                            onClick={() => handleAction(tenant.id, 'suspend')}
                            disabled={actionLoading}
                            className="text-[#EF4444] hover:underline text-xs font-medium disabled:opacity-50"
                            data-testid={`suspend-${tenant.id}`}
                          >
                            Suspend
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(tenant.id, tenant.company_name)}
                          disabled={actionLoading}
                          className="text-[#EF4444] hover:underline text-xs font-medium disabled:opacity-50"
                          data-testid={`delete-${tenant.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50" data-testid="tenant-detail-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-center">
              <h2 className="text-xl font-bold text-[#1a1a2e]">Tenant Details</h2>
              <button
                onClick={() => setSelectedTenant(null)}
                className="text-[#6b7280] hover:text-[#1a1a2e]"
                data-testid="close-modal-button"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-xs text-[#6b7280] mb-1">Company Name</p>
                  <p className="text-sm font-medium text-[#1a1a2e]">{selectedTenant.company_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6b7280] mb-1">NTN</p>
                  <p className="text-sm font-medium text-[#1a1a2e]">{selectedTenant.company_ntn || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6b7280] mb-1">Phone</p>
                  <p className="text-sm font-medium text-[#1a1a2e]">{selectedTenant.company_phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6b7280] mb-1">City</p>
                  <p className="text-sm font-medium text-[#1a1a2e]">{selectedTenant.company_city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6b7280] mb-1">Status</p>
                  <p className="text-sm font-medium text-[#1a1a2e] capitalize">{selectedTenant.subscription_status}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6b7280] mb-1">Plan</p>
                  <p className="text-sm font-medium text-[#1a1a2e] capitalize">{selectedTenant.subscription_plan}</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-[#1a1a2e] mb-3">Users</h3>
                <div className="space-y-2">
                  {selectedTenant.users.map((user) => (
                    <div key={user.id} className="flex justify-between items-center p-3 bg-[#f9fafb] rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-[#1a1a2e]">{user.full_name}</p>
                        <p className="text-xs text-[#6b7280]">{user.email}</p>
                      </div>
                      <span className="text-xs font-medium text-[#1E40AF] capitalize">{user.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;